// Membership tier type - matches database enum
export type MembershipTier = 'explorer' | 'creator' | 'collector' | 'connector' | 'curator';

// Tier capabilities - centralized definition for easy modification
export interface TierCapabilities {
  canBrowseExplore: boolean;
  canCreateCollections: boolean;
  canMakePrivate: boolean;
  canReceiveNotifications: boolean;
  canSendNotifications: boolean;
  canUseFavorites: boolean;
  canUseChat: boolean;
  canUseMarketplace: boolean;
  canUseMatchWizard: boolean;
}

// Tier hierarchy for comparison
export const TIER_HIERARCHY: Record<MembershipTier, number> = {
  explorer: 0,
  creator: 1,
  collector: 2,
  connector: 3,
  curator: 4,
};

// Centralized capabilities matrix - single source of truth
// Modify this to move features between tiers
export const TIER_CAPABILITIES: Record<MembershipTier, TierCapabilities> = {
  explorer: {
    canBrowseExplore: true,
    canCreateCollections: false,
    canMakePrivate: false,
    canReceiveNotifications: true,
    canSendNotifications: true,
    canUseFavorites: false,
    canUseChat: false,
    canUseMarketplace: false,
    canUseMatchWizard: false,
  },
  creator: {
    canBrowseExplore: true,
    canCreateCollections: true,
    canMakePrivate: false,
    canReceiveNotifications: true,
    canSendNotifications: true,
    canUseFavorites: true,
    canUseChat: false,
    canUseMarketplace: false,
    canUseMatchWizard: false,
  },
  collector: {
    canBrowseExplore: true,
    canCreateCollections: true,
    canMakePrivate: true,
    canReceiveNotifications: true,
    canSendNotifications: true,
    canUseFavorites: true,
    canUseChat: false,
    canUseMarketplace: false,
    canUseMatchWizard: false,
  },
  connector: {
    canBrowseExplore: true,
    canCreateCollections: true,
    canMakePrivate: true,
    canReceiveNotifications: true,
    canSendNotifications: true,
    canUseFavorites: true,
    canUseChat: true,
    canUseMarketplace: false,
    canUseMatchWizard: false,
  },
  curator: {
    canBrowseExplore: true,
    canCreateCollections: true,
    canMakePrivate: true,
    canReceiveNotifications: true,
    canSendNotifications: true,
    canUseFavorites: true,
    canUseChat: true,
    canUseMarketplace: true,
    canUseMatchWizard: true,
  },
};

// Get capabilities for a tier
export function getCapabilities(tier: MembershipTier): TierCapabilities {
  return TIER_CAPABILITIES[tier];
}

// Check if a tier meets the minimum required tier
export function meetsMinimumTier(userTier: MembershipTier, requiredTier: MembershipTier): boolean {
  return TIER_HIERARCHY[userTier] >= TIER_HIERARCHY[requiredTier];
}

// Tier display information
export interface TierInfo {
  name: string;
  title: string;
  description: string;
  features: string[];
}

export const TIER_INFO: Record<MembershipTier, TierInfo> = {
  explorer: {
    name: 'explorer',
    title: 'Explorer',
    description: 'Browse public collections and items from the community',
    features: [
      'View public collections',
      'Browse the Explore page',
      'View item details',
      'Send interest notifications',
    ],
  },
  creator: {
    name: 'creator',
    title: 'Creator',
    description: 'Create and share your own collections with the world',
    features: [
      'Everything in Explorer',
      'Create your own collections',
      'Add and manage items',
      'All collections are public',
      'Save favorites',
    ],
  },
  collector: {
    name: 'collector',
    title: 'Collector',
    description: 'Full control over your collections with privacy options',
    features: [
      'Everything in Creator',
      'Make collections private',
      'Control item visibility',
    ],
  },
  connector: {
    name: 'connector',
    title: 'Connector',
    description: 'Connect with other collectors and build your network',
    features: [
      'Everything in Collector',
      'Chat with other collectors',
    ],
  },
  curator: {
    name: 'curator',
    title: 'Curator',
    description: 'Advanced tools for serious collectors and traders',
    features: [
      'Everything in Connector',
      'Access to Marketplace',
      'Match Wizard for trade discovery',
      'Automated matching notifications',
      'Priority visibility in marketplace',
    ],
  },
};
